import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X,
  Clock,
  BarChart2,
  Users,
  AlertTriangle,
  Target,
  FileText,
  Shield,
  Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { PublicNavbar } from "@/components/layout/PublicNavbar";
import { Footer } from "@/components/layout/Footer";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { api } from "@/lib/api";

const HERO_IMAGE =
  "https://images.pexels.com/photos/30596263/pexels-photo-30596263.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FOUNDER_IMAGE =
  "https://images.pexels.com/photos/5716001/pexels-photo-5716001.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1400";

export default function Landing() {
  const navigate = useNavigate();
  const [showAccessForm, setShowAccessForm] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicNavbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "right center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/30" />
        <div className="absolute inset-0 grid-bg opacity-[0.15]" />

        <div className="relative max-w-[1280px] mx-auto px-6 md:px-8 py-20 md:py-32 grid md:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div className="fade-up">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-10 bg-[hsl(var(--gold))]" />
              <span className="overline gold-text">Deals. Intelligence. Execution.</span>
            </div>
            <h1 className="font-display font-medium text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-foreground">
              Access Frontier<br />
              Market Deals<br />
              <span className="text-muted-foreground">Before the Market</span><br />
              Prices Them
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Private deal intelligence, structuring insights, and execution pathways across
              Africa and MENA — built from real transactions, not theory.
            </p>

            <div className="mt-10 flex items-center gap-4">
              <Landmark className="h-8 w-8 gold-text" strokeWidth={1.5} />
              <div>
                <div className="font-mono text-3xl font-medium gold-text">$2B+</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  In transactions executed<br />across 90+ deals
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/auth")}
                className="btn-primary"
                data-testid="hero-request-access-button"
              >
                Request Access <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate("/sample")}
                className="btn-ghost"
                data-testid="hero-view-sample-button"
              >
                View Sample Intelligence <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="hidden md:block" />
        </div>

        <div className="relative border-t border-border">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-6 text-center">
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-muted-foreground">
              Trusted by investment professionals, family offices, fund managers
              <br className="hidden md:block" /> and operators deploying capital in frontier markets
            </p>
          </div>
        </div>
      </section>

      {/* NOT A NEWSLETTER */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <span className="overline gold-text">This is not a newsletter</span>
            <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl mt-4 tracking-tight">
              This Is Institutional<br />Intelligence
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Frontera Intelligence is a private platform for serious capital allocators.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Real transactions (not recycled news)",
                "Actual pricing and structuring logic",
                "Execution pathways to enter deals",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="h-4 w-4 gold-text mt-1 shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm text-muted-foreground leading-relaxed max-w-md">
              If you are allocating capital into Africa and MENA —<br />
              this is where you get your edge.
            </p>
          </div>

          <div className="card-institutional divide-y divide-border" id="what-you-get">
            {[
              { icon: Target, title: "Real Deals", body: "Early visibility on high-quality opportunities before they are widely known." },
              { icon: FileText, title: "Real Pricing", body: "Understand how deals are actually priced and where returns are made." },
              { icon: Users, title: "Real Execution", body: "Clear pathways, key counterparties, and how to position for allocation." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 p-6">
                <div className="h-12 w-12 shrink-0 border border-[hsl(var(--gold))] rounded-md flex items-center justify-center">
                  <Icon className="h-5 w-5 gold-text" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display font-medium text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY INVESTORS FAIL */}
      <section className="border-y border-border surface py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl text-center tracking-tight">
            Why Most Investors Fail in Frontier Markets
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {[
              { icon: Clock, title: "They see deals too late", body: "By the time deals are public, the best returns are already priced out." },
              { icon: BarChart2, title: "They don't understand structuring", body: "Poor structuring leads to lower returns and hidden risks." },
              { icon: Users, title: "They rely on intermediaries with misaligned incentives", body: "Information asymmetry leads to overpaying for deals." },
              { icon: AlertTriangle, title: "They underestimate risk and overpay", body: "The result is capital loss, low returns, or no access at all." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="card-institutional p-6">
                <Icon className="h-6 w-6 gold-text mb-6" strokeWidth={1.5} />
                <h3 className="font-display font-medium text-base leading-snug">{title}</h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 max-w-3xl mx-auto card-institutional px-6 md:px-10 py-6 md:py-8 text-center">
            <p className="font-display text-lg md:text-2xl leading-snug">
              Information is not the problem.
            </p>
            <p className="font-display text-lg md:text-2xl leading-snug gold-text">
              Access to the right information is.
            </p>
          </div>
        </div>
      </section>

      {/* EDGE + SAMPLE */}
      <section id="sample" className="max-w-[1280px] mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
          <div>
            <span className="overline gold-text">Your Unfair Advantage</span>
            <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl mt-4 tracking-tight">
              From Insight<br />to Allocation
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Frontera Intelligence gives you the layer between hearing about a deal and
              allocating at the right price.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Early visibility on transactions",
                "Clear pricing frameworks",
                "Structuring insights used by institutions",
                "Direct pathways into execution",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="h-4 w-4 gold-text mt-1 shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/auth")}
              className="btn-primary mt-8"
              data-testid="edge-request-access-button"
            >
              Request Access <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Sample deal card — matches screenshot */}
          <div className="card-institutional p-6 md:p-8" data-testid="sample-deal-card">
            <div className="flex items-center justify-between mb-5">
              <span className="overline gold-text">Sample Intelligence Snapshot</span>
              <StatusBadge status="pre_market" />
            </div>
            <h3 className="font-display font-medium text-2xl md:text-3xl tracking-tight">
              Kenya Sovereign–Linked Facility
            </h3>
            <div className="divider my-6" />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { k: "Size", v: "$500M" },
                { k: "Tenor", v: "7 Years" },
                { k: "Return", v: "SOFR + 750bps" },
                { k: "Status", v: "Pre-Market" },
              ].map((m) => (
                <div key={m.k}>
                  <div className="overline">{m.k}</div>
                  <div className="font-mono text-base md:text-lg mt-1.5">{m.v}</div>
                </div>
              ))}
            </div>

            <div className="divider my-6" />

            <div className="space-y-3 text-sm">
              {[
                ["Structure", "Offshore SPV + securitized revenue stream"],
                ["Key Risk", "FX volatility + political cycle timing"],
                ["Insight", "Mispriced relative to Eurobond curve by ~120bps"],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[110px_1fr] gap-4">
                  <div className="text-muted-foreground">{k}</div>
                  <div>{v}</div>
                </div>
              ))}
            </div>

            <div className="divider my-6" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 border border-border rounded flex items-center justify-center">
                  <FileText className="h-4 w-4 gold-text" strokeWidth={1.5} />
                </div>
                <div className="text-xs">
                  <div>Full breakdown available inside</div>
                  <div className="text-muted-foreground">Frontera Intelligence</div>
                </div>
              </div>
              <button
                onClick={() => navigate("/sample")}
                className="text-sm gold-text hover:underline flex items-center gap-1"
                data-testid="view-full-breakdown-button"
              >
                View Full Breakdown <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-y border-border surface py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center max-w-xl mx-auto">
            <span className="overline gold-text">Access Tiers</span>
            <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl mt-4 tracking-tight">
              Designed for Allocators,<br />Priced for Access
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              {
                name: "Standard Access",
                sub: "For individual investors and operators",
                price: "$79",
                period: "/month",
                features: ["Weekly intelligence drops", "Deal summaries", "Deep-dive memos"],
                cta: "Subscribe Now",
                tier: "standard",
                highlight: false,
              },
              {
                name: "Professional Access",
                sub: "For active allocators",
                price: "$199",
                period: "/month",
                features: ["Full deal breakdowns", "Execution pathways", "Priority insights"],
                cta: "Subscribe Now",
                tier: "professional",
                highlight: true,
              },
              {
                name: "Institutional / Deal Room",
                sub: "For serious capital deployment",
                price: "By Application",
                period: "Only",
                features: ["Live deal pipeline", "Direct introductions (curated)", "Monthly private call", "Allocation pathways"],
                cta: "Apply for Access",
                tier: "institutional",
                highlight: false,
              },
            ].map((p) => (
              <div
                key={p.name}
                className={`card-institutional p-6 md:p-8 relative flex flex-col ${
                  p.highlight ? "border-[hsl(var(--gold))]" : ""
                }`}
                data-testid={`pricing-card-${p.tier}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 gold-bg text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1 rounded-full text-[hsl(var(--accent-foreground))]">
                    Most Popular
                  </div>
                )}
                <h3 className="font-display font-medium text-xl">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.sub}</p>
                <div className="divider my-5" />
                <ul className="space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 gold-text mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display font-medium text-3xl md:text-4xl">{p.price}</span>
                    <span className="text-sm text-muted-foreground">{p.period}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/auth", { state: { tier: p.tier } })}
                  className={`mt-5 w-full ${p.highlight ? "btn-primary" : "btn-ghost"}`}
                  data-testid={`pricing-cta-${p.tier}`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section id="who-its-for" className="max-w-[1280px] mx-auto px-6 md:px-8 py-20 md:py-28">
        <span className="overline gold-text">Who It's For</span>
        <div className="grid md:grid-cols-2 gap-12 mt-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full border border-[hsl(var(--status-live))] flex items-center justify-center">
                <Check className="h-4 w-4" style={{ color: "hsl(var(--status-live))" }} />
              </div>
              <h3 className="font-display font-medium text-xl">This Is Built For</h3>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              {[
                "Family offices",
                "Entrepreneurs deploying capital",
                "Investment managers",
                "Professionals entering Africa/MENA markets",
                "Sovereign / quasi-sovereign allocators",
              ].map((i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full border border-[hsl(var(--status-closed))] flex items-center justify-center">
                <X className="h-4 w-4" style={{ color: "hsl(var(--status-closed))" }} />
              </div>
              <h3 className="font-display font-medium text-xl">This Is Not For</h3>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              {[
                "Passive readers",
                "Those not actively allocating capital",
                "Beginners",
                "Speculative retail investors",
              ].map((i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">{i}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="border-y border-border">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-20 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <AppMockup />
          <div>
            <span className="overline gold-text">Built by a Practitioner</span>
            <h2 className="font-display font-medium text-3xl md:text-4xl mt-4 tracking-tight">
              Experience That Comes From Doing
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Frontera Intelligence is built from direct experience in structuring and executing
              over <span className="text-foreground font-mono">$2B+</span> in transactions across
              Africa and global markets.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              This is not theory. <span className="gold-text">This is how deals actually get done.</span>
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-8 py-24 text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Shield className="h-5 w-5 gold-text" strokeWidth={1.5} />
          <span className="overline gold-text">Limited Membership</span>
        </div>
        <h2 className="font-display font-medium text-3xl md:text-5xl lg:text-6xl tracking-tight">
          If You Allocate Capital,<br />
          <span className="text-muted-foreground">This Is Where You Start</span>
        </h2>
        <p className="mt-6 text-sm text-muted-foreground max-w-lg mx-auto">
          Founding members receive preferential access to future deal participation opportunities.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button onClick={() => navigate("/auth")} className="btn-primary" data-testid="final-cta-request-access">
            Request Access <ArrowRight className="h-4 w-4" />
          </button>
          <button onClick={() => navigate("/sample")} className="btn-ghost" data-testid="final-cta-view-sample">
            View Sample Intelligence <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// -------- Institutional app mockup (inline, no external image) --------
const MOCK_DEALS = [
  { country: "Kenya", title: "Sovereign Facility", size: "$500M", tenor: "7yr", ret: "14–16%", status: "pre_market" },
  { country: "Nigeria", title: "Upstream Senior Note", size: "$25M", tenor: "5yr", ret: "10–12%", status: "live" },
  { country: "Rwanda", title: "MinFin Bilateral", size: "$80M", tenor: "6yr", ret: "11–13%", status: "live" },
  { country: "Egypt", title: "Subordinated Note", size: "$150M", tenor: "5yr", ret: "13–15%", status: "pre_market" },
  { country: "Botswana", title: "BoB Composite", size: "$120M", tenor: "8yr", ret: "9–11%", status: "closed" },
];

const STATUS_DOT = {
  live: "bg-[hsl(var(--status-live))]",
  pre_market: "bg-[hsl(var(--gold))]",
  closed: "bg-muted-foreground/50",
};

function AppMockup() {
  return (
    <div
      className="relative rounded-xl overflow-hidden border border-border shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] bg-[hsl(var(--surface-elevated))]"
      data-testid="landing-app-mockup"
    >
      {/* Subtle gold glow */}
      <div className="absolute -inset-1 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--gold)/0.15),transparent_55%)] pointer-events-none" />

      {/* Browser chrome */}
      <div className="relative flex items-center gap-1.5 px-4 py-3 border-b border-border bg-background/60">
        <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <div className="ml-4 flex-1 h-6 rounded-md bg-background border border-border px-3 flex items-center">
          <span className="font-mono text-[10px] text-muted-foreground truncate">
            frontera.io/deals · live pipeline
          </span>
        </div>
      </div>

      {/* App shell */}
      <div className="relative grid grid-cols-[120px_1fr] min-h-[360px]">
        {/* Sidebar */}
        <div className="hidden md:block border-r border-border p-3 bg-background/40">
          <div className="font-display text-[10px] tracking-[0.2em] gold-text mb-4">FRONTERA</div>
          {["Dashboard", "Deal Flow", "Intelligence", "Playbooks", "Deal Room", "Admin"].map((s, i) => (
            <div
              key={s}
              className={`text-[10px] px-2 py-1.5 rounded mb-0.5 font-mono tracking-wide ${
                i === 1
                  ? "bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))]/30 text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {s.toUpperCase()}
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div className="p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-[9px]">Deal Flow</div>
              <div className="font-display text-lg md:text-xl tracking-tight mt-0.5">Live Pipeline</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-live))] animate-pulse" />
              <span className="font-mono text-[10px] gold-text">29 ACTIVE</span>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { k: "AVG RETURN", v: "12.4%" },
              { k: "CAPITAL FLOW", v: "$2.1B" },
              { k: "RISK INDEX", v: "B+" },
            ].map((m) => (
              <div key={m.k} className="border border-border rounded-md px-2.5 py-2 bg-background/50">
                <div className="font-mono text-[8px] text-muted-foreground tracking-wider">{m.k}</div>
                <div className="font-mono text-sm mt-0.5 gold-text">{m.v}</div>
              </div>
            ))}
          </div>

          {/* Deal rows */}
          <div className="border border-border rounded-md divide-y divide-border overflow-hidden bg-background/40">
            <div className="grid grid-cols-[16px_1fr_50px_50px_60px_40px] gap-2 px-3 py-1.5 bg-background/70">
              {["", "DEAL", "SIZE", "TENOR", "RETURN", ""].map((h, i) => (
                <div key={i} className="font-mono text-[8px] text-muted-foreground tracking-wider">{h}</div>
              ))}
            </div>
            {MOCK_DEALS.map((d) => (
              <div
                key={d.title}
                className="grid grid-cols-[16px_1fr_50px_50px_60px_40px] gap-2 px-3 py-2 items-center"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[d.status]}`} />
                <div className="min-w-0">
                  <div className="text-[11px] font-medium truncate">{d.title}</div>
                  <div className="font-mono text-[9px] text-muted-foreground">{d.country}</div>
                </div>
                <div className="font-mono text-[10px]">{d.size}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{d.tenor}</div>
                <div className="font-mono text-[10px] gold-text">{d.ret}</div>
                <div className="text-right font-mono text-[9px] text-muted-foreground">→</div>
              </div>
            ))}
          </div>

          {/* Bottom insight strip */}
          <div className="mt-4 rounded-md border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 px-3 py-2">
            <div className="overline text-[8px] gold-text">Pricing Insight · Kenya Sovereign</div>
            <div className="text-[10px] leading-snug mt-1 text-foreground/90">
              Secondary market prints 180bps wide of primary — adjusted for rating migration, embedded convexity adds 1.8% yield without duration extension.
            </div>
          </div>
        </div>
      </div>

      {/* Caption chip */}
      <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full border border-[hsl(var(--gold))]/40 bg-background/70 backdrop-blur-sm">
        <div className="font-mono text-[9px] gold-text tracking-wider">$2B+ EXECUTED</div>
      </div>
    </div>
  );
}
